import React, { useState, useEffect } from "react";
import { Form, Input, Button, message } from "antd";
import { SafetyOutlined } from "@ant-design/icons";
import { Auth } from "aws-amplify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LeftPanel } from "./SignIn";
import "./AuthPages.css";

const RESEND_COOLDOWN = 60;

const VerifyEmail: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN);
  const [resending, setResending] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const email = searchParams.get("email") || "";

  useEffect(() => {
    if (!email) {
      message.error("No email provided. Please sign up again.");
      navigate("/signup?from=activation");
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleVerify = async (values: { code: string }) => {
    try {
      setLoading(true);
      await Auth.confirmSignUp(email, values.code.trim());
      message.success("Email verified! You can now sign in.");
      navigate("/");
    } catch (error: any) {
      console.error("Verification error:", error);
      let errMsg = "Verification failed";
      if (error.code === "CodeMismatchException") {
        errMsg = "Invalid code. Please check your email and try again.";
      } else if (error.code === "ExpiredCodeException") {
        errMsg = "Code has expired. Please request a new one.";
      } else if (error.code === "NotAuthorizedException") {
        errMsg = "Account already confirmed. Please sign in.";
        setTimeout(() => navigate("/"), 1500);
      } else if (error.message) {
        errMsg = error.message;
      }
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setResending(true);
      await Auth.resendSignUp(email);
      message.success("Verification code resent. Check your email.");
      setResendCooldown(RESEND_COOLDOWN);
    } catch (error: any) {
      console.error("Resend error:", error);
      message.error(error.message || "Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  const particles = Array.from({ length: 6 }, (_, i) => (
    <div key={i} className="auth-particle" />
  ));

  return (
    <div className="auth-page">
      {particles}
      <LeftPanel />
      <div className="auth-right-panel">
        <div className="auth-card">
          <div className="auth-card-header">
            <div className="auth-card-title">Verify Your Email</div>
            <div className="auth-card-subtitle">
              We sent a 6-digit code to <strong>{email}</strong>
            </div>
          </div>

          <Form form={form} layout="vertical" onFinish={handleVerify}>
            <div className="auth-field">
              <label className="auth-label">Verification Code</label>
              <div className="auth-input-wrap">
                <SafetyOutlined className="auth-input-icon" />
                <Form.Item
                  name="code"
                  rules={[
                    { required: true, message: "Code is required" },
                    {
                      pattern: /^\d{6}$/,
                      message: "Must be a 6-digit code",
                    },
                  ]}
                  noStyle
                >
                  <Input
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </Form.Item>
              </div>
            </div>

            <Form.Item>
              <Button
                className="auth-submit-btn"
                type="primary"
                htmlType="submit"
                loading={loading}
                block
              >
                Verify Email
              </Button>
            </Form.Item>
          </Form>

          <div className="auth-footer">
            Didn't receive the code?{" "}
            <button
              type="button"
              disabled={resendCooldown > 0 || resending}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
                color: resendCooldown > 0 ? "var(--auth-text-dim)" : "var(--auth-accent)",
                fontWeight: 500,
                fontSize: 14,
                fontFamily: "inherit",
                opacity: resendCooldown > 0 ? 0.6 : 1,
              }}
              onClick={handleResend}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
            </button>
          </div>

          <div className="auth-footer" style={{ marginTop: 8 }}>
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "var(--auth-accent)",
                fontWeight: 500,
                fontSize: 14,
                fontFamily: "inherit",
              }}
              onClick={() => navigate("/")}
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
